import * as React from 'react';
import {Box, IconButton, Typography, styled, useTheme} from '@mui/material';

import {useSelector} from 'react-redux';
import {selectSelectedProcessSteps, selectSelectedL1Label} from 'app/store/appState/appStateSlice';

import {Grid, Stack} from '@mui/material';
import RACIBucket from './RACIBucket';
import RACIBucketTTDOM from './RACIBucketTTDOM';
import RACIRoleFilters from './RACIRoleFilter';
import {IStep, ProcessTabs, RACI_ClassNames} from 'app/types/Shared';
import {Close, Refresh} from '@mui/icons-material';
import {EmptyState} from 'components/EmptyState/EmptyState';
import grips from 'assets/images/grips180.png';
//styled box to hide scrollbar on horizontal scroll
const StyledBox = styled(Box)({
  overflowY: 'auto',
  scrollbarWidth: 'none' /* Firefox */,
  overflow: 'scroll',
  '&::-webkit-scrollbar': {
    display: 'none',
  } /* Chrome */,
  msOverflowStyle: 'none',
});

type filter = {
  roleAbbr: string;
  roleName: string;
  selected: boolean;
};

type RACIProps = {
  onL1Click?: (subprocess: number, label: string) => void;
  onL2Click?: (id: string) => void;
  processID: string;
  simplified?: boolean;
  storybook?: boolean;
  ttdom?: boolean;
  handleTabChange?: (tabIndex: number, tab: ProcessTabs) => void;
};

const RACIView = ({
  onL1Click,
  onL2Click,
  processID,
  simplified = false,
  storybook = false,
  ttdom = false,
  handleTabChange,
}: RACIProps) => {
  const theme = useTheme();
  const data = useSelector(selectSelectedProcessSteps);
  const bucket = useSelector(selectSelectedL1Label);

  const process = React.useRef(processID);

  const [FilterDictionary] = React.useState<{[key: string]: Array<filter>}>({
    [RACI_ClassNames.CRO]: [],
    [RACI_ClassNames.oTAK]: [],
    [RACI_ClassNames.TAK]: [],
  });
  const [CROSize, setCROSize] = React.useState<number>(0); //number of  CRO rows in grid
  const [OTAKSize, setOTAKSize] = React.useState<number>(0); //number of  OTAK rows in grid
  const [TAKSize, setTAKSize] = React.useState<number>(0); //number of  TAK rows in grid
  const [toggle, setToggle] = React.useState<boolean>(false);

  //load dictonary on component mount
  React.useEffect(() => {
    if (data && data.length > 0) {
      if (process.current !== processID) {
        FilterDictionary[RACI_ClassNames.CRO].length = 0;
        FilterDictionary[RACI_ClassNames.oTAK].length = 0;
        FilterDictionary[RACI_ClassNames.TAK].length = 0;
        process.current = processID;
      }
      data.map((step) => {
        step.raci
          .filter((x) => x.raci_list)
          .map((raciItem) => {
            const defaultFilter: filter = {
              roleAbbr: raciItem.role.role_category.abbrev,
              roleName: raciItem.role.role_category.label
                ? raciItem.role.role_category.label
                : raciItem.role.role_category.name,
              selected: false,
            };
            const filters = FilterDictionary[raciItem.role.class_name];
            if (filters && !filters.some((item) => item.roleAbbr === defaultFilter.roleAbbr)) {
              filters.push(defaultFilter);
            }
          });
      });
    }
    setCROSize(FilterDictionary[RACI_ClassNames.CRO].length);
    setOTAKSize(FilterDictionary[RACI_ClassNames.oTAK].length);
    setTAKSize(FilterDictionary[RACI_ClassNames.TAK].length);

    if (FilterDictionary[RACI_ClassNames.CRO].length > 0) {
      FilterDictionary[RACI_ClassNames.CRO].sort(compareByType);
    }
    if (FilterDictionary[RACI_ClassNames.oTAK].length > 0) {
      FilterDictionary[RACI_ClassNames.oTAK].sort(compareByType);
    }
    if (FilterDictionary[RACI_ClassNames.TAK].length > 0) {
      FilterDictionary[RACI_ClassNames.TAK].sort(compareByType);
    }
  }, [FilterDictionary, data, process, processID]);

  const RaciBuckets = data?.reduce((acc, curr) => {
    if (curr.level === 1) {
      acc.push({title: curr.level_label ? curr.level_label : curr.desc, items: []});
    } else if ((curr?.level === 2 || (curr?.level === 3 && curr.raci.length > 0)) && acc.length > 0) {
      acc[acc.length - 1].items.push(curr);
    } else if (ttdom && curr?.level === 3 && curr?.order && curr?.order < 20000 && acc.length > 0) {
      acc[acc.length - 1].items.push(curr);
    }
    return acc;
  }, [] as Array<{title: string; items: Array<IStep>}>);

  const compareByType = (a: {roleAbbr: string}, b: {roleAbbr: string}): number => {
    if (a.roleAbbr?.toLowerCase() < b.roleAbbr?.toLowerCase()) {
      return -1;
    } else if (a.roleAbbr?.toLowerCase() > b.roleAbbr?.toLowerCase()) {
      return 1;
    } else {
      return 0;
    }
  };

  const hasSelected = () => {
    let flag = false;
    Object.keys(FilterDictionary).forEach((obj) => {
      if (FilterDictionary[obj].some((x) => x.selected === true)) {
        flag = true;
      }
    });
    return flag;
  };

  //set current selected filters in dictonary
  const setFilters = (type: string, lookup: string) => {
    const index = FilterDictionary[lookup].findIndex((item) => item.roleAbbr === type);

    if (index >= 0) {
      const selectedFilter = FilterDictionary[lookup]?.at(index);
      if (selectedFilter && selectedFilter.selected) {
        selectedFilter.selected = false;
      } else if (selectedFilter) {
        selectedFilter.selected = true;
      }
    }
  };
  //toggle to force rerender
  const updateToggle = () => {
    if (toggle) setToggle(false);
    else setToggle(true);
  };

  const resetFilters = () => {
    Object.keys(FilterDictionary).forEach((key) => {
      FilterDictionary[key].map((filter) => {
        filter.selected = false;
      });
    });
    updateToggle();
  };

  const getPreSteps = () => {
    const firstL1 = data?.findIndex((x) => x.level === 1);
    if (firstL1 === -1) return data?.filter((x) => x?.order && x?.order < 20000);
    return data?.slice(0, firstL1);
  };
  const StyledGrid = styled(Grid)({
    overflowX: 'auto',
    cursor: 'grab',
    '&::-webkit-scrollbar': {
      height: '13px',
      borderWidth: '5px 0px 5px 0px',
    } /* Chrome */,
    '&::-webkit-scrollbar-track': {
      background: '#DDDFE3',
      borderWidth: '3px 0px 3px 0px',
      borderStyle: 'solid',
      borderColor: '#f6f8f9',
      boxShadow: '0px 0px 2px 0px rgba(0, 0, 0, 0.07) inset',
    } /* Chrome */,
    '&::-webkit-scrollbar-thumb': {
      backgroundImage: `url(${grips})`,
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundColor: 'white',
      border: '2px solid #DDDFE3',
      borderRadius: '2px',
      minHeight: '50%',
      minWidth: '50%',
    },
  });
  const [showCount, setShowCount] = React.useState(false);

  const ref = React.useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      if (node.scrollWidth > node.clientWidth) {
        setShowCount(true);
      } else {
        setShowCount(false);
      }
    }
  }, []);

  // if (ref) {
  // }
  let columns = 14 + (CROSize + TAKSize + OTAKSize);
  if (ttdom) {
    columns += 3;
  }
  if (showCount) {
    columns += 3;
  }

  return (
    <>
      {data?.length ? (
        <StyledBox px={6} pb={2} pt={6} sx={{minWidth: '1025px'}}>
          <Box>
            <Stack spacing={0.5} direction='column'>
              <Stack direction='row' alignItems='flex-end' pb='8px'>
                <Grid container direction='row' columns={columns}>
                  <Grid item xs={7} sx={{minWidth: '25% !important'}} id='offset'>
                    <Box
                      display='flex'
                      justifyContent='flex-end'
                      alignItems='center'
                      height='100%'
                      width='100%'
                      pt='12px'
                    >
                      {hasSelected() && (
                        <IconButton onClick={() => resetFilters()} sx={{py: 0, px: 0}}>
                          <Refresh fontSize='large' sx={{color: theme.palette.grey[800], transform: 'scaleX(-1)'}} />
                        </IconButton>
                      )}
                    </Box>
                  </Grid>
                  <StyledGrid
                    ref={ref}
                    onScroll={(e) => {
                      const elems = Array.prototype.slice.call(document.getElementsByClassName('test-ref'));
                      elems.forEach((elem) => {
                        elem.scrollTo((e.target as HTMLElement).scrollLeft, 0);
                      });
                    }}
                    item
                    xs={CROSize + TAKSize + OTAKSize}
                    columns={CROSize + TAKSize + OTAKSize}
                    style={{
                      display: 'grid',
                      gridAutoFlow: 'column',
                      maxWidth: `calc(75% - max(378px, ${((showCount ? 10 : 7) / columns) * 100}%) - ${
                        ((ttdom ? 3 : 0) * 100) / columns
                      }%)`,
                      // minWidth: CROSize + TAKSize + OTAKSize < 10 ? 100 * (CROSize + TAKSize + OTAKSize) : '200px',
                      overflowX: 'auto',
                      justifyContent: 'flex-start',
                      paddingBottom: '10px',
                    }}
                  >
                    {CROSize > 0 && (
                      <Grid
                        key={RACI_ClassNames.CRO}
                        item
                        xs={CROSize}
                        sx={{
                          minWidth: CROSize * 100,
                        }}
                      >
                        <RACIRoleFilters
                          title='CRO'
                          size={1}
                          lookup={RACI_ClassNames.CRO}
                          callback2={setFilters}
                          FilterDictionary={FilterDictionary}
                          callback={updateToggle}
                        />
                      </Grid>
                    )}
                    {OTAKSize > 0 && (
                      <Grid
                        key={RACI_ClassNames.oTAK}
                        item
                        xs={OTAKSize}
                        sx={{
                          minWidth: OTAKSize * 100,
                        }}
                      >
                        <RACIRoleFilters
                          title='oTakeda'
                          size={OTAKSize}
                          lookup={RACI_ClassNames.oTAK}
                          callback2={setFilters}
                          FilterDictionary={FilterDictionary}
                          callback={updateToggle}
                        />
                      </Grid>
                    )}
                    {TAKSize > 0 && (
                      <Grid
                        key={RACI_ClassNames.TAK}
                        item
                        xs={TAKSize}
                        sx={{
                          minWidth: TAKSize * 100,
                        }}
                      >
                        <RACIRoleFilters
                          title='Takeda'
                          size={TAKSize}
                          lookup={RACI_ClassNames.TAK}
                          callback2={setFilters}
                          FilterDictionary={FilterDictionary}
                          callback={updateToggle}
                        />
                      </Grid>
                    )}
                  </StyledGrid>
                  {showCount && (
                    <Grid item xs={3} sx={{minWidth: 100}}>
                      <Box display='flex' alignItems='flex-end' sx={{height: 88, alignItems: 'center'}}>
                        <Box
                          sx={{
                            width: '100%',
                            height: 10,
                            color: theme.palette.grey[400],
                            // bgcolor: theme.palette.grey[800],
                            justifyContent: 'flex-start',
                            // alignItems: 'center',
                            display: 'flex',
                            pl: 1,
                          }}
                        >
                          <Typography variant='h5' color='black'>
                            {CROSize + TAKSize + OTAKSize} Roles
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={7} sx={{minWidth: 200}}>
                    <Box display='flex' alignItems='flex-end' sx={{height: 50}}></Box>
                  </Grid>

                  {ttdom && (
                    <Grid item xs={3} sx={{minWidth: 100}}>
                      <Box display='flex' alignItems='flex-end' sx={{height: 88}}>
                        <Box
                          sx={{
                            width: '100%',
                            height: 35,
                            bgcolor: theme.palette.grey[800],
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            display: 'flex',
                            pl: 1,
                          }}
                        >
                          <Typography variant='h5' color='white'>
                            Flags
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
                <Box
                  sx={{
                    width: '12px',
                    mb: '12.5px',
                    height: 35,
                    bgcolor: ttdom ? theme.palette.grey[800] : theme.palette.grey[100],
                  }}
                />
              </Stack>
              {RaciBuckets?.length ? (
                RaciBuckets.map((b) => (
                  <React.Fragment key={b.title}>
                    {ttdom ? (
                      <RACIBucketTTDOM
                        title={b.title}
                        items={b.items}
                        FilterDictionary={FilterDictionary}
                        CROSize={CROSize}
                        TAKSize={TAKSize}
                        OTAKSize={OTAKSize}
                        showCount={showCount}
                        bucket={bucket}
                        l1_steps={RaciBuckets?.map((x) => x.title)}
                        onL1Click={onL1Click}
                        onL2Click={onL2Click}
                        handleTabChange={handleTabChange}
                        storybook={storybook}
                        preSteps={getPreSteps()}
                      />
                    ) : (
                      <RACIBucket
                        showCount={showCount}
                        title={b.title}
                        items={b.items}
                        FilterDictionary={FilterDictionary}
                        CROSize={CROSize}
                        TAKSize={TAKSize}
                        OTAKSize={OTAKSize}
                        bucket={bucket}
                        onL1Click={onL1Click}
                        onL2Click={onL2Click}
                        simplified={simplified}
                        handleTabChange={handleTabChange}
                        storybook={storybook}
                      />
                    )}
                  </React.Fragment>
                ))
              ) : (
                <RACIBucketTTDOM
                  title=''
                  showCount={showCount}
                  items={[]}
                  FilterDictionary={FilterDictionary}
                  CROSize={CROSize}
                  TAKSize={TAKSize}
                  OTAKSize={OTAKSize}
                  l1_steps={['']}
                  bucket={''}
                  onL1Click={onL1Click}
                  onL2Click={onL2Click}
                  handleTabChange={handleTabChange}
                  storybook={storybook}
                  preSteps={getPreSteps()}
                />
              )}
            </Stack>
          </Box>
        </StyledBox>
      ) : (
        <Box width='100%' height='15vw' display='flex' alignItems='center' justifyContent='center'>
          <EmptyState
            icon={<Close color='primary' fontSize='medium' />}
            title='Data not defined'
            subtitle='This process has not been loaded yet'
          />
        </Box>
      )}
    </>
  );
};

export default RACIView;
